import Reveal from "./Reveal";
import { Instagram, MapPin, Mail, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site-data";

const TikTokIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.9a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-1.33Z" />
  </svg>
);

const QUICK = [
  { label: "Home", id: "home" },
  { label: "Services", id: "services" },
  { label: "About", id: "about" },
  { label: "Reviews", id: "results" },
  { label: "Book Now", id: "book" },
];

const SERVICE_LINKS = [
  "Korean Lash Lifts",
  "Brow Lamination & Tint",
  "Skin Treatments",
];

export default function Footer() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="relative bg-[color:var(--sr-black)] border-t border-[var(--sr-border)] pt-20 pb-8"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div>
            <img
              src="/images/logo.png"
              alt="Skin Refinery by Lacretia"
              className="h-28 md:h-36 w-auto object-contain -ml-1"
            />
            <p className="mt-4 text-[color:var(--sr-beige)] text-[13.5px] leading-[1.75] max-w-[280px]">
              {SITE.owner} &middot; {SITE.title}
              <br />
              Korean skincare. Lashes. Brows. Confidence that lasts.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — @skinrefinery.417"
                data-testid="social-instagram"
                className="w-10 h-10 border border-[var(--sr-border-strong)] rounded-full flex items-center justify-center hover:border-[color:var(--sr-gold)] hover:text-[color:var(--sr-gold)] transition-colors text-[color:var(--sr-ivory)]"
              >
                <Instagram size={14} strokeWidth={1.4} />
              </a>
              <a
                href={SITE.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok — @skinrefinery.417"
                data-testid="social-tiktok"
                className="w-10 h-10 border border-[var(--sr-border-strong)] rounded-full flex items-center justify-center hover:border-[color:var(--sr-gold)] hover:text-[color:var(--sr-gold)] transition-colors text-[color:var(--sr-ivory)]"
              >
                <TikTokIcon size={14} />
              </a>
            </div>
            <p className="mt-4 eyebrow text-[color:var(--sr-beige)]/80">
              @skinrefinery.417
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="eyebrow text-[color:var(--sr-ivory)]">Explore</h4>
            <ul className="mt-6 space-y-3">
              {QUICK.map((q) => (
                <li key={q.id}>
                  <a
                    href={`#${q.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo(q.id);
                    }}
                    className="text-[color:var(--sr-beige)] text-[13.5px] hover:text-[color:var(--sr-gold)] transition-colors"
                  >
                    {q.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="eyebrow text-[color:var(--sr-ivory)]">Services</h4>
            <ul className="mt-6 space-y-3">
              {SERVICE_LINKS.map((s) => (
                <li key={s}>
                  <a
                    href="#services"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo("services");
                    }}
                    className="text-[color:var(--sr-beige)] text-[13.5px] hover:text-[color:var(--sr-gold)] transition-colors"
                  >
                    {s}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[color:var(--sr-gold)] text-[13.5px] hover:opacity-80 transition-opacity"
                >
                  Full service menu
                  <ArrowUpRight size={12} strokeWidth={1.5} />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="eyebrow text-[color:var(--sr-ivory)]">Visit</h4>
            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3 text-[color:var(--sr-beige)] text-[13.5px]">
                <MapPin
                  size={14}
                  className="mt-1 text-[color:var(--sr-gold)] shrink-0"
                  strokeWidth={1.4}
                />
                <span>
                  3897 N 20th St. Unit 1<br />
                  Ozark, MO
                  <br />
                  <span className="text-[color:var(--sr-beige)]/70">
                    By Appointment Only
                  </span>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail
                  size={14}
                  className="text-[color:var(--sr-gold)]"
                  strokeWidth={1.4}
                />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email"
                  className="text-[color:var(--sr-beige)] text-[13.5px] hover:text-[color:var(--sr-gold)] transition-colors break-all"
                >
                  {SITE.email}
                </a>
              </li>
              <li className="pt-2">
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-book-now"
                  className="btn-gold"
                >
                  Book Now
                </a>
              </li>
            </ul>
          </div>
        </Reveal>

        <div className="mt-16 pt-8 border-t border-[var(--sr-border)] text-center">
          <p
            className="eyebrow text-[color:var(--sr-beige)]/70"
            data-testid="footer-copyright"
          >
            © 2026 Skin Refinery by Lacretia. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
